import styled from "styled-components";
import { breakpoints, colors } from "definitions";

const CARD_RADIUS = "0.8rem";
const CARD_SHADOW = "0 4px 16px rgba(0, 0, 0, 0.08)";

export const Container = styled.div`
  max-width: 124.5rem;
  width: 95%;
  padding-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${breakpoints.tablet} {
    width: 90%;
  }
`;

// Un bloque por tipo de contenido (finalistas, menciones, galería): título +
// una card por premio/mención.
export const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  margin-bottom: 4rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${colors.background.white};
  border-radius: ${CARD_RADIUS};
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  padding: 1.4rem 2.4rem;
  background-color: ${colors.fonts.gray};
  color: ${colors.fonts.white};
  font-weight: 600;
  font-size: 1.8rem;
  line-height: 2.2rem;

  ${breakpoints.phablet} {
    padding: 1.2rem 1.6rem;
    font-size: 1.5rem;
    line-height: 2rem;
  }
`;

// Las tarjetas de una misma fila se estiran a la misma altura, sin el alto fijo
// que dejaba la mitad de cada tarjeta vacía.
export const FinalistsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 2rem;
  padding: 3rem 2.4rem;
  width: 100%;

  ${breakpoints.phablet} {
    padding: 2rem 1.2rem;
  }
`;

export const FinalistItem = styled.article`
  display: flex;
  flex-direction: column;
  width: 27rem;
  min-height: 20rem;
  background-color: ${colors.background.white};
  border-radius: ${CARD_RADIUS};
  border: 1px solid
    ${({ winner }) =>
      winner ? colors.fonts.gray : colors.background.veryLightGray};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  }

  ${breakpoints.phablet} {
    width: 100%;
  }
`;

// El color ya distinguía ganador de finalista pero sin decirlo: ahora lo dice.
export const FinalistItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.2rem;
  background-color: ${({ winner }) =>
    winner ? colors.fonts.gray : colors.background.veryLightGray};
  color: ${({ winner }) =>
    winner ? colors.fonts.white : colors.fonts.darkGray};
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const FinalistItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 2rem;
`;

export const FinalistItemTitle = styled.span`
  font-size: 1.1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.fonts.darkGray};
  opacity: 0.7;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid ${colors.background.veryLightGray};
`;

export const FinalistItemContent = styled.p`
  margin: 0 0 1.6rem;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  color: ${colors.fonts.veryDarkGray};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MentionBody = styled.div`
  padding: 2.4rem;

  p {
    font-size: 1.4rem;
    line-height: 2.4rem;
    margin: 0 0 1.2rem;
  }

  ul {
    font-size: 1.4rem;
    line-height: 2.4rem;
    width: 100%;
    margin: 0;
    padding-left: 2rem;

    li {
      margin: 0.8rem 0;
    }
  }

  ${breakpoints.phablet} {
    padding: 1.6rem;
  }
`;

export const GalleryContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  max-height: 50rem;
  overflow: hidden;
  border-radius: ${CARD_RADIUS};
  box-shadow: ${CARD_SHADOW};

  ${breakpoints.tabletXL} {
    height: 25rem;
  }

  ${breakpoints.phablet} {
    height: 15rem;
  }
`;

// Se muestra cuando el año elegido no tiene nada publicado, en lugar de dejar la
// página en blanco hasta el footer.
export const EmptyState = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 6rem 2rem;
  text-align: center;
  background-color: ${colors.background.white};
  border: 1px dashed ${colors.navigation.lightGray};
  border-radius: ${CARD_RADIUS};
  color: ${colors.fonts.darkGray};

  strong {
    font-size: 2.2rem;
    line-height: 2.8rem;
    color: ${colors.fonts.gray};
  }

  span {
    font-size: 1.5rem;
    line-height: 2.2rem;
    max-width: 48rem;
  }
`;
